import _ from 'lodash';
import { FormattedDate, FormattedMessage } from 'react-intl';

import styles from './RepaymentScheduleTable.module.css';
import { FormattedCurrency } from '../../../../../iwIntlProvider/FormattedCurrency';
import IwMedia from '../../../../../iwMedia/IwMedia';
import { NormalisedRepaymentSchedule } from '../../../MyOffers.types';

const RepaymentScheduleTable = ({
    repaymentSchedule
}: {
    repaymentSchedule: NormalisedRepaymentSchedule;
}) => {
    const renderTableHeader = () => {
        return (
            <tr>
                <IwMedia to="small">
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.date"
                            defaultMessage="Date"
                        />
                    </th>
                    <th colSpan={2}>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.split"
                            defaultMessage="Split"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.total"
                            defaultMessage="Total"
                        />
                    </th>
                </IwMedia>
                <IwMedia from="small">
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.date"
                            defaultMessage="Date"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.principal"
                            defaultMessage="Principal"
                        />
                    </th>
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.interest"
                            defaultMessage="Interest"
                        />
                    </th>
                    {repaymentSchedule.hasFees && (
                        <th>
                            <FormattedMessage
                                id="_.modal.loan_builder.repayment_schedule.table.fees"
                                defaultMessage="Fees"
                            />
                        </th>
                    )}
                    <th>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.totalRepayment"
                            defaultMessage="Total Repayment"
                        />
                    </th>
                </IwMedia>
            </tr>
        );
    };
    const renderTableBody = () => {
        const rows = _.map(repaymentSchedule.repayments, (repayment) => {
            return (
                <tr key={repayment.date} data-testid="repayment-row">
                    <IwMedia to="small">
                        <td className={styles.date}>
                            <FormattedDate
                                value={new Date(repayment.date)}
                                day="numeric"
                                month="numeric"
                                year="2-digit"
                            />
                        </td>
                        <td colSpan={2}>
                            <span className={styles.principalLabel}>
                                <FormattedMessage
                                    id="_.modal.loan_builder.repayment_schedule.table.principal"
                                    defaultMessage="Principal"
                                />
                                :
                            </span>
                            <FormattedCurrency value={repayment.principal} />
                            <br />
                            <span className={styles.interestLabel}>
                                <FormattedMessage
                                    id="_.modal.loan_builder.repayment_schedule.table.interest"
                                    defaultMessage="Interest"
                                />
                                :
                            </span>
                            <FormattedCurrency value={repayment.interest} />
                            {repaymentSchedule.hasFees && (
                                <>
                                    <br />
                                    <span className={styles.feesLabel}>
                                        <FormattedMessage
                                            id="_.modal.loan_builder.repayment_schedule.table.fees"
                                            defaultMessage="Fees"
                                        />
                                        :
                                    </span>
                                    <FormattedCurrency
                                        value={repayment.total_fees}
                                    />
                                </>
                            )}
                        </td>
                        <td className={styles.total}>
                            <FormattedCurrency value={repayment.total} />
                        </td>
                    </IwMedia>
                    <IwMedia from="small">
                        <td>
                            <FormattedDate value={new Date(repayment.date)} />
                        </td>
                        <td>
                            <FormattedCurrency value={repayment.principal} />
                        </td>
                        <td>
                            <FormattedCurrency value={repayment.interest} />
                        </td>
                        {repaymentSchedule.hasFees && (
                            <td>
                                <FormattedCurrency
                                    value={repayment.total_fees}
                                />
                            </td>
                        )}
                        <td>
                            <FormattedCurrency value={repayment.total} />
                        </td>
                    </IwMedia>
                </tr>
            );
        });

        if (repaymentSchedule.repayments.length < 12) {
            return [
                ...rows,
                <tr key="early-repayment-info">
                    <td colSpan={4}>
                        <div className={styles.earlyRepayment}>
                            <p>
                                <FormattedMessage
                                    id="_.modal.loan_builder.repayment_schedule.table.pay_off"
                                    defaultMessage="To pay off your loan early, just make a bank transfer or a one-off payment in your iwoca account."
                                />
                            </p>
                            <p>
                                <FormattedMessage
                                    id="_.modal.loan_builder.repayment_schedule.table.extra_payments"
                                    defaultMessage="You can also make extra payments throughout to bring your outstanding amount down."
                                />
                            </p>
                        </div>
                    </td>
                </tr>
            ];
        }

        return rows;
    };

    const renderTableFooter = () => {
        return (
            <tr>
                <IwMedia to="small">
                    <td colSpan={3}>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.total"
                            defaultMessage="Total"
                        />
                        :
                    </td>
                </IwMedia>
                <IwMedia from="small">
                    <td>
                        <FormattedMessage
                            id="_.modal.loan_builder.repayment_schedule.table.total"
                            defaultMessage="Total"
                        />
                        :
                    </td>
                    <td data-testid="total-principal">
                        <FormattedCurrency
                            // @ts-expect-error
                            value={repaymentSchedule.totalPrincipal}
                        />
                    </td>
                    <td data-testid="total-interest">
                        <FormattedCurrency
                            // @ts-expect-error
                            value={repaymentSchedule.totalInterest}
                        />
                    </td>
                    {repaymentSchedule.hasFees && (
                        <td>
                            <FormattedCurrency
                                // @ts-expect-error
                                value={repaymentSchedule.totalFees}
                            />
                        </td>
                    )}
                </IwMedia>
                <td data-testid="total-balance">
                    <FormattedCurrency
                        // @ts-expect-error
                        value={repaymentSchedule.totalRepayment}
                    />
                </td>
            </tr>
        );
    };

    return (
        <div className={styles.RepaymentScheduleTable}>
            <table>
                <thead>{renderTableHeader()}</thead>
                <tbody>{renderTableBody()}</tbody>
                <tfoot>{renderTableFooter()}</tfoot>
            </table>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default RepaymentScheduleTable;
